import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "@/lib/api";
import StepBasicInfo from "./StepBasicInfo";
import StepSchedules from "./StepSchedules";
import StepTeam from "./StepTeam";
import StepContacts from "./StepContacts";
import StepReview from "./StepReview";
import { type ChurchFormData, type ScheduleEntry, type TeamMember, type ContactEntry, emptyFormData } from "../types";

const STEPS = [
  { label: "Infos", icon: "ri-building-4-line", shortTitle: "Informations générales" },
  { label: "Horaires", icon: "ri-time-line", shortTitle: "Horaires & Confessions" },
  { label: "Équipe", icon: "ri-group-2-line", shortTitle: "Équipe presbytérale" },
  { label: "Contacts", icon: "ri-phone-line", shortTitle: "Contacts de la paroisse" },
  { label: "Révision", icon: "ri-eye-line", shortTitle: "Récapitulatif" },
];

function updateListItem<T>(list: T[], index: number, update: Partial<T>): T[] {
  return list.map((item, i) => (i === index ? { ...item, ...update } : item));
}

export default function AddChurchWizard({
  embedded = false,
  onClose,
}: {
  embedded?: boolean;
  onClose?: () => void;
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState<ChurchFormData>(emptyFormData());
  const [submitted, setSubmitted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof ChurchFormData | "submit", string>>>({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field: keyof ChurchFormData, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const makeScheduleHandlers = (field: "massSchedule" | "confessionSchedule" | "officeHours") => ({
    onChange: (i: number, key: "day" | "time", value: string) => {
      setFormData((prev) => ({
        ...prev,
        [field]: updateListItem(prev[field] as ScheduleEntry[], i, { [key]: value }),
      }));
    },
    onAdd: () => {
      setFormData((prev) => ({
        ...prev,
        [field]: [...(prev[field] as ScheduleEntry[]), { day: "", time: "" }],
      }));
    },
    onRemove: (i: number) => {
      setFormData((prev) => ({
        ...prev,
        [field]: (prev[field] as ScheduleEntry[]).filter((_, idx) => idx !== i),
      }));
    },
  });

  const massHandlers = makeScheduleHandlers("massSchedule");
  const confHandlers = makeScheduleHandlers("confessionSchedule");
  const officeHandlers = makeScheduleHandlers("officeHours");

  const handleTeamChange = (i: number, field: keyof TeamMember, value: string) => {
    setFormData((prev) => ({
      ...prev,
      team: updateListItem(prev.team, i, { [field]: value }),
    }));
  };
  const handleTeamAdd = () => {
    setFormData((prev) => ({
      ...prev,
      team: [...prev.team, { name: "", role: "Curé", image: "" }],
    }));
  };
  const handleTeamRemove = (i: number) => {
    setFormData((prev) => ({
      ...prev,
      team: prev.team.filter((_, idx) => idx !== i),
    }));
  };

  const handleContactChange = (i: number, field: keyof ContactEntry, value: string) => {
    setFormData((prev) => ({
      ...prev,
      contacts: updateListItem(prev.contacts, i, { [field]: value }),
    }));
  };
  const handleContactAdd = () => {
    setFormData((prev) => ({
      ...prev,
      contacts: [...prev.contacts, { type: "Téléphone", value: "" }],
    }));
  };
  const handleContactRemove = (i: number) => {
    setFormData((prev) => ({
      ...prev,
      contacts: prev.contacts.filter((_, idx) => idx !== i),
    }));
  };

  const validate = (): Partial<Record<keyof ChurchFormData, string>> => {
    const errs: Partial<Record<keyof ChurchFormData, string>> = {};
    if (!formData.name.trim()) errs.name = "Le nom de l'église est requis.";
    if (!formData.diocese) errs.diocese = "Le diocèse est requis.";
    if (!formData.city.trim()) errs.city = "La ville est requise.";
    if (!formData.address.trim()) errs.address = "L'adresse est requise.";
    if (!formData.imageFile) errs.imageFile = "L'image principale est requise.";
    return errs;
  };

  const handleNext = () => {
    if (step === 0) {
      const errs = validate();
      if (Object.keys(errs).length > 0) { setFieldErrors(errs); return; }
      setFieldErrors({});
    }
    if (step < STEPS.length - 1) setStep((s) => s + 1);
  };

  const handleBack = () => {
    if (step > 0) {
      setStep((s) => s - 1);
      return;
    }

    if (onClose) {
      onClose();
      return;
    }

    navigate(-1);
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setFieldErrors(errs); setStep(0); return; }

    const payload = new FormData();
    payload.append("nom", formData.name);
    payload.append("diocese", formData.diocese);
    payload.append("ville", formData.city);
    payload.append("adresse", formData.address);
    if (formData.description.trim()) payload.append("description", formData.description);
    if (formData.imageFile) payload.append("image_file", formData.imageFile);
    if (formData.videoFile) payload.append("video_file", formData.videoFile);
    if (formData.massSchedule.some((entry) => entry.day && entry.time)) {
      formData.massSchedule.filter((entry) => entry.day && entry.time).forEach((entry, index) => {
        payload.append(`horaire_messe[${index}][jour]`, entry.day);
        payload.append(`horaire_messe[${index}][heure]`, entry.time);
      });
    }
    if (formData.confessionSchedule.some((entry) => entry.day && entry.time)) {
      formData.confessionSchedule.filter((entry) => entry.day && entry.time).forEach((entry, index) => {
        payload.append(`horaire_confession[${index}][jour]`, entry.day);
        payload.append(`horaire_confession[${index}][heure]`, entry.time);
      });
    }
    if (formData.officeHours.some((entry) => entry.day && entry.time)) {
      formData.officeHours.filter((entry) => entry.day && entry.time).forEach((entry, index) => {
        payload.append(`bureau_schedule[${index}][jour]`, entry.day);
        payload.append(`bureau_schedule[${index}][heure]`, entry.time);
      });
      payload.append("horaire_bureau", formData.officeHours.filter((entry) => entry.day && entry.time).map((entry) => `${entry.day}: ${entry.time}`).join(" | "));
    }
    formData.contacts.filter((entry) => entry.value.trim()).forEach((entry, index) => {
      payload.append(`contacts[${index}][type]`, entry.type);
      payload.append(`contacts[${index}][value]`, entry.value);
    });
    formData.team.filter((entry) => entry.name.trim()).forEach((entry, index) => {
      payload.append(`equipe[${index}][nom]`, entry.name);
      payload.append(`equipe[${index}][role]`, entry.role);
      if (entry.image.trim()) payload.append(`equipe[${index}][image]`, entry.image);
    });

    setSubmitting(true);
    try {
      await apiRequest("/eglises", {
        method: "POST",
        body: payload,
      });
      setSubmitted(true);
    } catch (error) {
      setFieldErrors({ submit: error instanceof Error ? error.message : "Impossible d'ajouter l'église." });
      setStep(0);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    setFormData(emptyFormData());
    setStep(0);
    setFieldErrors({});
    setSubmitting(false);
  };

  const progressPct = ((step + 1) / STEPS.length) * 100;
  const shellClass = embedded
    ? "overflow-hidden rounded-[28px] border border-gray-100 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.08)]"
    : "min-h-screen bg-[#F4F5F7]";
  const bodyClass = embedded
    ? "bg-[#F4F5F7] min-h-full px-4 pt-4 pb-32"
    : "bg-[#F4F5F7] min-h-full px-4 pt-4 pb-32";
  const footerClass = embedded
    ? "sticky bottom-0 inset-x-0 z-30 bg-white border-t border-stone-100 px-4 py-4"
    : "fixed bottom-0 inset-x-0 z-50 bg-white border-t border-stone-100 px-4 py-4 pb-6";

  return (
    <div className={shellClass}>
      {submitted ? (
        <div className={`flex flex-col items-center justify-center px-6 text-center ${embedded ? "min-h-[540px] py-16" : "min-h-screen pb-24"}`}>
          <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full" style={{ background: "rgba(13,27,94,0.08)" }}>
            <i className="ri-checkbox-circle-fill text-4xl" style={{ color: "#0D1B5E" }} />
          </div>
          <h2 className="mb-2 text-xl font-bold text-stone-800" style={{ fontFamily: "'Playfair Display', serif" }}>
            Église ajoutée !
          </h2>
          <p className="mb-8 text-sm leading-relaxed text-stone-400" style={{ fontFamily: "'Nunito', sans-serif" }}>
            <strong className="text-stone-600">{formData.name}</strong> a été soumise avec succès.
            Elle sera vérifiée et publiée sous peu.
          </p>
          <button
            onClick={handleReset}
            className="mb-3 w-full rounded-2xl bg-stone-100 py-3.5 text-sm font-bold text-stone-600 cursor-pointer"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            Ajouter une autre église
          </button>
          <button
            onClick={() => onClose ? onClose() : navigate("/")}
            className="w-full rounded-2xl py-3.5 text-sm font-bold text-white cursor-pointer"
            style={{ background: "#0D1B5E", fontFamily: "'Nunito', sans-serif" }}
          >
            {onClose ? "Retour aux églises" : "Retour à l'accueil"}
          </button>
        </div>
      ) : (
        <>
          <div className="sticky top-0 z-40" style={{ background: "#0D1B3E" }}>
            <div className="flex items-center justify-between px-4 pt-6 pb-3">
              <button onClick={handleBack} className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 cursor-pointer">
                <i className="ri-arrow-left-line text-lg text-white" />
              </button>
              <div className="text-center">
                <h1 className="text-base font-bold leading-tight text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Ajouter une église
                </h1>
                <p className="mt-0.5 text-xs text-white/50" style={{ fontFamily: "'Nunito', sans-serif" }}>
                  Étape {step + 1} sur {STEPS.length} — {STEPS[step].shortTitle}
                </p>
              </div>
              <div className="w-9" />
            </div>

            <div className="mx-4 mb-3 h-1 rounded-full bg-white/10">
              <div className="h-full rounded-full transition-all duration-300" style={{ width: `${progressPct}%`, background: "#F59E0B" }} />
            </div>

            <div className="flex items-center justify-between gap-1 px-4 pb-3">
              {STEPS.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => { if (i < step) setStep(i); }}
                  className={`flex-1 cursor-pointer rounded-xl py-1.5 transition-all ${i === step ? "bg-white/15" : i < step ? "opacity-80" : "opacity-40"}`}
                >
                  <div className="flex flex-col items-center gap-0.5">
                    <div className="flex h-5 w-5 items-center justify-center">
                      {i < step ? <i className="ri-checkbox-circle-fill text-base text-amber-400" /> : <i className={`${s.icon} text-sm text-white`} />}
                    </div>
                    <span className={`text-xs leading-none ${i === step ? "font-bold text-white" : "text-white/60"}`} style={{ fontFamily: "'Nunito', sans-serif" }}>
                      {s.label}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className={bodyClass}>
            {fieldErrors.submit ? (
              <div className="mb-4 flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
                <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center">
                  <i className="ri-error-warning-line text-sm text-red-500" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-red-600" style={{ fontFamily: "'Nunito', sans-serif" }}>
                    {fieldErrors.submit}
                  </p>
                </div>
              </div>
            ) : null}

            {step === 0 && <StepBasicInfo data={formData} onChange={handleChange} errors={fieldErrors} />}
            {step === 1 && (
              <StepSchedules
                massSchedule={formData.massSchedule}
                confessionSchedule={formData.confessionSchedule}
                officeHours={formData.officeHours}
                onMassChange={massHandlers.onChange}
                onMassAdd={massHandlers.onAdd}
                onMassRemove={massHandlers.onRemove}
                onConfChange={confHandlers.onChange}
                onConfAdd={confHandlers.onAdd}
                onConfRemove={confHandlers.onRemove}
                onOfficeChange={officeHandlers.onChange}
                onOfficeAdd={officeHandlers.onAdd}
                onOfficeRemove={officeHandlers.onRemove}
              />
            )}
            {step === 2 && <StepTeam team={formData.team} onAdd={handleTeamAdd} onRemove={handleTeamRemove} onChange={handleTeamChange} />}
            {step === 3 && <StepContacts contacts={formData.contacts} onAdd={handleContactAdd} onRemove={handleContactRemove} onChange={handleContactChange} />}
            {step === 4 && <StepReview data={formData} />}
          </div>

          <div className={footerClass}>
            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={handleNext}
                className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl py-4 text-sm font-bold text-white transition-transform active:scale-95"
                style={{ background: "#0D1B5E", fontFamily: "'Nunito', sans-serif" }}
              >
                Continuer
                <div className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-arrow-right-line text-base text-white" />
                </div>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void handleSubmit()}
                disabled={submitting}
                className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl py-4 text-sm font-bold text-white transition-transform active:scale-95 disabled:cursor-not-allowed disabled:opacity-70"
                style={{ background: "#16a34a", fontFamily: "'Nunito', sans-serif" }}
              >
                <div className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-checkbox-circle-line text-base text-white" />
                </div>
                {submitting ? "Envoi en cours..." : "Soumettre l'église"}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
